/**
 * Helper functions for KPI Overview feature.
 * Extracted from KPIOverviewTab.tsx for better code organization.
 */

import type { KpiFormat } from './formatters';

// ─── Types ────────────────────────────────────────────────────────────────────

export type KpiCategory = 'Safety' | 'People' | 'Accuracy' | 'Reliability' | 'Cost' | 'Navigator';

export interface KpiMetric {
	name: string;
	dbName?: string;
	category: string;
	actual: number | null;
	target: number | null;
	delta: number | null;
	deltaMagnitude: number | null;
	isUnfavorable: boolean | null;
	format: KpiFormat;
	trend: number[];
}

export interface LinkedReportInfo {
	reportName: string;
	reportLink: string;
	platform: string;
}

// ─── Filter token helpers ─────────────────────────────────────────────────────

export const normalizeFilterToken = (value: unknown): string => {
	const normalized = String(value ?? '').trim();
	if (!normalized || normalized.toLowerCase() === 'all') {
		return '';
	}
	return normalized;
};

export const toNumericToken = (value: unknown): string => {
	const token = normalizeFilterToken(value);
	if (!token) {
		return '';
	}
	const match = token.match(/\d+/);
	return match?.[0] ?? token;
};

// ─── Delta helpers ────────────────────────────────────────────────────────────

export const getDeltaColor = (isUnfavorable: boolean): string => {
	if (isUnfavorable) {
		return '#D14343';
	}
	return '#198038';
};

export const getDisplayDelta = (delta: number | null): number | null => {
	return delta == null ? null : Math.abs(delta);
};

/**
 * Determine arrow direction based on actual vs target (not sign of delta).
 * Arrow-up means actual > target; arrow-down means actual < target.
 */

export const getDeltaArrowIcon = (actual: number | null, target: number | null): string => {
	if (actual == null || target == null) return 'remove';
	return actual > target ? 'arrow-drop-up' : actual < target ? 'arrow-drop-down' : 'remove';
};

// ─── Injury KPI special handling ──────────────────────────────────────────────

const INJURY_KPI_TOKEN = 'INJURY';

export const isInjuryKpi = (metricName: string): boolean => {
	return metricName.trim().toUpperCase().includes(INJURY_KPI_TOKEN);
};

// ─── Category helpers ─────────────────────────────────────────────────────────

export const KPI_CATEGORY_FILTER_ORDER: KpiCategory[] = ['Safety', 'People', 'Accuracy', 'Reliability', 'Cost', 'Navigator'];

export const KPI_CATEGORY_COLORS: Record<KpiCategory, { background: string; text: string; icon: string }> = {
	Safety: { background: '#FFE7E1', text: '#C43A17', icon: '#C43A17' },
	People: { background: '#ECE7FF', text: '#5B48C5', icon: '#5B48C5' },
	Accuracy: { background: '#E8F1FD', text: '#0B5FC6', icon: '#0B5FC6' },
	Reliability: { background: '#DFF4FA', text: '#19759A', icon: '#19759A' },
	Cost: { background: '#E2F8E0', text: '#0D7A39', icon: '#0D7A39' },
	Navigator: { background: '#C6DCFB', text: '#01346F', icon: '#01346F' },
};

/** Dark-mode variants — bright saturated tones legible on dark surfaces. */
export const KPI_CATEGORY_COLORS_DARK: Record<KpiCategory, { background: string; text: string; icon: string }> = {
	Safety:      { background: '#9B2626', text: '#FFFFFF', icon: '#FFB09A' },
	People:      { background: '#4A3FA0', text: '#FFFFFF', icon: '#CEC0FF' },
	Accuracy:    { background: '#1A5BB5', text: '#FFFFFF', icon: '#99CFFF' },
	Reliability: { background: '#1E5A7A', text: '#FFFFFF', icon: '#7DE8FF' },
	Cost:        { background: '#1A6B24', text: '#FFFFFF', icon: '#8FEBA8' },
	Navigator:   { background: '#24509A', text: '#FFFFFF', icon: '#AACCFF' },
};

const KPI_CATEGORY_MAP: Record<string, KpiCategory> = {
	SAFETY: 'Safety',
	S: 'Safety',
	PEOPLE: 'People',
	P: 'People',
	ACCURACY: 'Accuracy',
	A: 'Accuracy',
	RELIABILITY: 'Reliability',
	R: 'Reliability',
	COST: 'Cost',
	C: 'Cost',
	NAVIGATOR: 'Navigator',
	N: 'Navigator',
};

export const resolveKpiCategory = (category: string): KpiCategory | null => {
	return KPI_CATEGORY_MAP[category.trim().toUpperCase()] ?? null;
};

export const getCategoryBadgeColor = (category: string): string => {
	const token = category.trim().toUpperCase();

	const sparcCategoryColorMap: { [key: string]: string } = {
		SAFETY: '#D4F4DD',
		S: '#D4F4DD',
		ACCURACY: '#FFF4D4',
		A: '#FFF4D4',
		RELIABILITY: '#D4E4FF',
		R: '#D4E4FF',
		PEOPLE: '#E3F2FD',
		P: '#E3F2FD',
		COST: '#FDD4D4',
		C: '#FDD4D4',
		N: '#EAE6FF',
	};

	return sparcCategoryColorMap[token] || '#D4F4DD';
};

export const getCategoryBadgeLabel = (category: string): string => {
	const token = category.trim().toUpperCase();

	const sparcCategoryLabelMap: { [key: string]: string } = {
		SAFETY: 'S',
		S: 'S',
		ACCURACY: 'A',
		A: 'A',
		RELIABILITY: 'R',
		R: 'R',
		PEOPLE: 'P',
		P: 'P',
		COST: 'C',
		C: 'C',
		N: 'N',
	};

	if (sparcCategoryLabelMap[token]) {
		return sparcCategoryLabelMap[token];
	}

	return token.charAt(0) || 'S';
};

export const getCategoryIcon = (category: string): string => {
	switch (category.toUpperCase()) {
		case 'S':
			return 'person';
		case 'P':
			return 'show-chart';
		case 'A':
			return 'emoji-events';
		case 'R':
			return 'calendar-today';
		case 'C':
			return 'paid';
		case 'N':
			return 'insights';
		default:
			return 'bar-chart';
	}
};

export const getCategoryContentColor = (category: string): string => {
	const resolved = resolveKpiCategory(category);
	if (resolved) {
		return KPI_CATEGORY_COLORS[resolved].icon;
	}
	return '#1B5E20';
};

export const getCategoryBackgroundColor = (category: string): string => {
	const resolved = resolveKpiCategory(category);
	if (resolved) {
		return KPI_CATEGORY_COLORS[resolved].background;
	}
	return '#D4F4DD';
};

// ─── KPI Allowlist ────────────────────────────────────────────────────────────

const ALLOWED_KPI_NAMES: string[] = [
	'INJURY',
	'OVER TIME %',
	'TIME TO PUNCH',
	'GAP TIME',
	'BPA %',
	'SIA %',
	'IN HOUSE CUT',
	'ON TIME %',
	'PERFECT TRUCK EXECUTION',
	'REPACK %',
	'KCPH',
	'MLP %',
	'DPS %',
	'PKU',
	'HAULBACK %',
	'WH BREAK %',
	'RAPID BREAK $',
	'NAV EXCELLENCE',
];

const KPI_DISPLAY_NAME_OVERRIDES: Record<string, string> = {
};

export const isAllowedKpi = (metricName: string): boolean => {
	const upper = metricName.trim().toUpperCase();
	return ALLOWED_KPI_NAMES.some(allowed => upper.includes(allowed));
};

export const getDisplayName = (metricName: string): string => {
	const upper = metricName.trim().toUpperCase();
	if (upper.startsWith('GAP TIME')) {
		return 'Gap Time (Avg Min / Operator)';
	}
	if (KPI_DISPLAY_NAME_OVERRIDES[upper]) {
		return KPI_DISPLAY_NAME_OVERRIDES[upper];
	}
	return metricName;
};

// ─── Platform / Report helpers ────────────────────────────────────────────────

export const PLATFORM_BADGE_STYLES: Record<string, { background: string; text: string }> = {
	'Power BI': { background: '#FFF3D8', text: '#8A6500' },
	Tableau: { background: '#E5F6EA', text: '#2E7D32' },
	TBD: { background: '#ECEDEF', text: '#70757D' },
};

/** Dark-mode platform badge variants. */
export const PLATFORM_BADGE_STYLES_DARK: Record<string, { background: string; text: string }> = {
	'Power BI': { background: '#92600A', text: '#FFFFFF' },
	Tableau:    { background: '#1A6B24', text: '#FFFFFF' },
	TBD:        { background: '#4B4B4B', text: '#FFFFFF' },
};

export const inferPlatformFromLink = (link: string): string => {
	const normalized = link.trim().toLowerCase();
	if (!normalized || normalized === 'tbd') return 'TBD';
	if (normalized.includes('powerbi')) return 'Power BI';
	if (normalized.includes('tableau')) return 'Tableau';
	return 'TBD';
};
